import type { JsonSchemaType } from '@gmn-portal/backend';
import { Edit3 } from 'lucide-react';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useCustomer, useObject, useObjectCategory } from '@/core/api';
import { useAuth } from '@/core/auth';
import { useIntl } from '@/core/i18n/use-intl';
import { EntityReferenceValue } from '@/shared/components/layout';
import { MetadataReadView } from '@/shared/components/metadata/metadata-read-view';
import { Button, Card, CardContent } from '@/shared/components/ui';
import { getCustomerDetailHref, getObjectDetailHref } from '@/shared/utils';
import { getFieldPermission, parseMetadata } from '@/shared/utils/metadata';
import {
  canReadService,
  resolveUserGroup,
  resolveUserGroupDomain,
} from '@/shared/utils/permissions';
import type { Object as PortalViewObject } from '../types';

const formatDay = (value?: string | null) =>
  value ? DateTime.fromISO(value).toFormat('dd.MM.yyyy') : '-';

const getRelationshipId = (value: unknown): string | null => {
  if (!value) return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && 'id' in value && typeof value.id === 'string') {
    return value.id;
  }
  return null;
};

type SystemShipCurrentAssignmentPanelProps = {
  activeObjectId: string;
  occupiedPeriods: Array<{
    startDate: string;
    endDate: string;
    objectId: string | null;
  }>;
  systemShipId: string;
  sectionIds?: {
    assignment: string;
    periods: string;
  };
  metadataSections?: Array<{
    id: string;
    label: string;
    fieldNames: string[];
  }>;
};

export function SystemShipCurrentAssignmentPanel({
  activeObjectId,
  occupiedPeriods,
  sectionIds,
  metadataSections = [],
}: SystemShipCurrentAssignmentPanelProps) {
  const { t } = useIntl();
  const { user } = useAuth();
  const group = resolveUserGroup(user);
  const domain = resolveUserGroupDomain(user);
  const canReadObjects = canReadService(group, 'objects');
  const canReadCustomers = canReadService(group, 'customers');
  const { data: activeObject, isLoading } = useObject(activeObjectId, {
    disabled: !activeObjectId,
  });
  const categoryId = getRelationshipId(activeObject?.category);
  const { data: category } = useObjectCategory(categoryId || '', {
    disabled: !categoryId,
  });
  const categoryMetadataSchema = category?.metadataSchema as JsonSchemaType | undefined;
  const currentGroupKey = group?.key ?? null;

  const metadataCurrentObject = useMemo(
    () =>
      activeObject
        ? ({
            ...activeObject,
            description: activeObject.description ?? undefined,
          } as PortalViewObject)
        : null,
    [activeObject]
  );

  const occupiedShipPeriods = useMemo(
    () =>
      occupiedPeriods
        .filter((period) => period.objectId !== activeObjectId)
        .map((period) => ({
          startDate: period.startDate,
          endDate: period.endDate,
        })),
    [activeObjectId, occupiedPeriods]
  );

  const visibleMetadataFieldNames = useMemo(() => {
    if (!categoryMetadataSchema?.properties) {
      return [];
    }

    const propertyOrder =
      categoryMetadataSchema.propertyOrder || Object.keys(categoryMetadataSchema.properties);
    return propertyOrder.filter((fieldName) => {
      const field = categoryMetadataSchema.properties[fieldName];
      return getFieldPermission(field, currentGroupKey).read;
    });
  }, [categoryMetadataSchema, currentGroupKey]);

  const assignmentSectionId = sectionIds?.assignment ?? 'system-ship-current-assignment';
  const periodsSectionId = sectionIds?.periods ?? 'system-ship-assignment-periods';

  const currentCustomer =
    activeObject?.assignedCustomer && typeof activeObject.assignedCustomer === 'object'
      ? activeObject.assignedCustomer
      : null;
  const currentCustomerId = getRelationshipId(activeObject?.assignedCustomer);
  const shouldResolveCurrentCustomerById =
    Boolean(currentCustomerId) && typeof activeObject?.assignedCustomer !== 'object';
  const { data: currentCustomerById } = useCustomer(
    shouldResolveCurrentCustomerById ? (currentCustomerId ?? '') : ''
  );
  const resolvedCurrentCustomer = currentCustomer ?? currentCustomerById;
  const currentCustomerHref = getCustomerDetailHref({
    customerId: currentCustomerId ?? resolvedCurrentCustomer?.id,
    domain,
    canReadCustomers,
  });
  const assignedHeliProvider =
    activeObject?.assignedHelicopterServiceProvider &&
    typeof activeObject.assignedHelicopterServiceProvider === 'object'
      ? activeObject.assignedHelicopterServiceProvider
      : null;
  const assignedHeliProviderId = getRelationshipId(activeObject?.assignedHelicopterServiceProvider);
  const shouldResolveAssignedHeliProviderById =
    Boolean(assignedHeliProviderId) &&
    typeof activeObject?.assignedHelicopterServiceProvider !== 'object';
  const { data: assignedHeliProviderById } = useCustomer(
    shouldResolveAssignedHeliProviderById ? (assignedHeliProviderId ?? '') : ''
  );
  const resolvedAssignedHeliProvider = assignedHeliProvider ?? assignedHeliProviderById;
  const assignedHeliProviderHref = getCustomerDetailHref({
    customerId: assignedHeliProviderId ?? resolvedAssignedHeliProvider?.id,
    domain,
    canReadCustomers,
  });
  const linkedWindpark =
    activeObject?.linkedWindpark && typeof activeObject.linkedWindpark === 'object'
      ? activeObject.linkedWindpark
      : null;
  const linkedWindparkId = getRelationshipId(activeObject?.linkedWindpark);
  const shouldResolveLinkedWindparkById =
    Boolean(linkedWindparkId) && typeof activeObject?.linkedWindpark !== 'object';
  const { data: linkedWindparkById } = useObject(linkedWindparkId ?? '', {
    disabled: !shouldResolveLinkedWindparkById,
  });
  const resolvedLinkedWindpark = linkedWindpark ?? linkedWindparkById;
  const linkedWindparkHref = getObjectDetailHref({
    objectId: linkedWindparkId ?? resolvedLinkedWindpark?.id,
    domain,
    canReadObjects,
  });
  const activeObjectHref = getObjectDetailHref({
    objectId: activeObjectId,
    domain,
    canReadObjects,
  });
  const activeAssignmentHref = activeObjectHref ? `${activeObjectHref}?assignment=1` : null;

  if (isLoading || !activeObject) {
    return (
      <Card>
        <CardContent className="pt-5 text-sm text-muted-foreground">
          {t('common.loading')}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="space-y-1">
            <div id={assignmentSectionId} className="flex items-center gap-3 scroll-mt-28">
              <h4 className="text-sm font-semibold text-foreground">
                {t('objects.ships.currentAssignment')}
              </h4>
              <div className="h-px flex-1 bg-border" />
            </div>
            <p className="text-sm text-muted-foreground">
              {t('objects.ships.currentAssignmentDescription')}
            </p>
          </div>
          {activeAssignmentHref ? (
            <Button asChild variant="outline" size="sm">
              <Link to={activeAssignmentHref}>
                <Edit3 className="mr-2 size-4" />
                {t('objects.ships.openAssignment')}
              </Link>
            </Button>
          ) : null}
        </div>

        <div className="grid grid-cols-6 gap-4">
          {domain !== 'customer' ? (
            <div className="col-span-6 md:col-span-3 lg:col-span-2">
              <div className="rounded-xl border border-border bg-card p-4">
                <div className="mb-1 text-xs font-medium text-muted-foreground">
                  {t('customers.customer')}
                </div>
                <div className="text-sm font-medium">
                  <EntityReferenceValue
                    label={resolvedCurrentCustomer?.name}
                    href={currentCustomerHref}
                  />
                </div>
              </div>
            </div>
          ) : null}

          <div className="col-span-6 rounded-xl border border-border bg-card p-4 md:col-span-3 lg:col-span-2">
            <div className="mb-1 text-xs font-medium text-muted-foreground">
              {t('customers.assignedHelicopterServiceProvider')}
            </div>
            <div className="text-sm font-medium">
              <EntityReferenceValue
                label={resolvedAssignedHeliProvider?.name}
                href={assignedHeliProviderHref}
              />
            </div>
          </div>

          <div className="col-span-6 rounded-xl border border-border bg-card p-4 md:col-span-3 lg:col-span-2">
            <div className="mb-1 text-xs font-medium text-muted-foreground">
              {t('objects.linkedWindpark')}
            </div>
            <div className="text-sm font-medium">
              <EntityReferenceValue
                label={resolvedLinkedWindpark?.name}
                href={linkedWindparkHref}
              />
            </div>
          </div>

          <div className="col-span-6 rounded-xl border border-border bg-card p-4 md:col-span-6 lg:col-span-2">
            <div className="mb-1 text-xs font-medium text-muted-foreground">
              {t('common.description')}
            </div>
            <div className="whitespace-pre-wrap break-words text-sm">
              {activeObject.description || '-'}
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div id={periodsSectionId} className="flex items-center gap-3 scroll-mt-28">
          <h4 className="text-sm font-semibold text-foreground">
            {t('objects.ships.activePeriodsTitle')}
          </h4>
          <div className="h-px flex-1 bg-border" />
        </div>

        <div className="space-y-4 rounded-xl border border-border bg-card p-4">
          {occupiedShipPeriods.length > 0 ? (
            <div className="rounded-lg border border-dashed border-border bg-background/70 px-3 py-3">
              <div className="text-xs font-medium text-muted-foreground">
                {t('objects.ships.occupiedPeriods')}
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {occupiedShipPeriods.map((period, index) => (
                  <span
                    key={`${period.startDate}-${period.endDate}-${index}`}
                    className="rounded-full bg-secondary px-3 py-1 text-xs font-medium"
                  >
                    {formatDay(period.startDate)} - {formatDay(period.endDate)}
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          <div className="text-sm font-medium">
            {(activeObject.activePeriods || [])
              .map((period) => `${formatDay(period.startDate)} - ${formatDay(period.endDate)}`)
              .join(', ') || '-'}
          </div>
        </div>
      </div>

      {categoryMetadataSchema &&
      (metadataSections.length > 0 || visibleMetadataFieldNames.length > 0) ? (
        metadataSections.length > 0 ? (
          <div className="space-y-10">
            {metadataSections.map((section) => (
              <div key={section.id} className="space-y-4">
                <div id={section.id} className="flex items-center gap-3 scroll-mt-28">
                  <h4 className="text-sm font-semibold text-foreground">{section.label}</h4>
                  <div className="h-px flex-1 bg-border" />
                </div>
                <MetadataReadView
                  metadataSchema={categoryMetadataSchema}
                  values={parseMetadata(
                    activeObject.categoryMetadataValues as Record<string, unknown> | undefined
                  )}
                  fieldNames={section.fieldNames}
                  groupKey={currentGroupKey}
                  currentObject={metadataCurrentObject}
                  canReadObjects={canReadObjects}
                  domain={domain}
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <h4 className="text-sm font-semibold text-foreground">{t('objects.attributes')}</h4>
              <div className="h-px flex-1 bg-border" />
            </div>
            <MetadataReadView
              metadataSchema={categoryMetadataSchema}
              values={parseMetadata(
                activeObject.categoryMetadataValues as Record<string, unknown> | undefined
              )}
              fieldNames={visibleMetadataFieldNames}
              groupKey={currentGroupKey}
              currentObject={metadataCurrentObject}
              canReadObjects={canReadObjects}
              domain={domain}
            />
          </div>
        )
      ) : null}
    </div>
  );
}
